import React, { useCallback } from "react";
import NavigationItem from "./navigationItem";
import { Badge, Icon, Icons, Span } from "@ir/ui";
import "./navigation.scss";
import { NavigationItemProps } from "./types/navigation.interface";
import useAppNavigation from "./hooks/useAppNavigation";
import { WorkspacePermissions } from "@/core/permissions";
import { useUserPermissions } from "@/hooks";
import { HasAllPermissions } from "@/presentation/workspace";
import SidebarFeedback from "../sidebar/sidebar.feedback";
import { useConversations } from "@/presentation/pages/conversations/hooks";
import { AppConfig, FlagsConfigInstance, WIPFlags } from "@/infrastructure/services/config";

const appConfig = AppConfig.getInstance();

const NAVIGATION_ITEMS: NavigationItemProps[] = [
  {
    key: "home",
    label: "Home",
    icon: <Icon name={Icons.Home} />,
    link: "/workspace/home",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.HOME],
  },
  {
    key: "deductions",
    label: "Deductions",
    icon: <Icon name={Icons.UserActivity} />,
    link: "/workspace/deductions",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.DEDUCTIONS],
  },
  {
    key: "approvals",
    label: "Approval Queue",
    icon: <Icon name={Icons.CheckDone} />,
    link: "/workspace/approvals",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.APPROVALS],
  },
  {
    key: "intelliUpload",
    label: "intelliUpload",
    icon: <Icon name={Icons.FilePlus} />,
    link: "/workspace/intelliUpload",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.INTELLI_UPLOAD],
  },
  {
    key: "conversations",
    label: "Conversations",
    icon: <Icon name={Icons.MessageChatCircle} />,
    link: "/workspace/conversations",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.CONVERSATIONS],
    isActive: true,
  },
  {
    key: "accounting",
    label: "Accounting Queue",
    icon: <Icon name={Icons.Calculator} />,
    link: "/workspace/accounting",
    workspaceRequiredPermissions: [
      WorkspacePermissions.MENU_VIEW.ACCOUNTING,
      WorkspacePermissions.ACCOUNTING_CREDIT_MEMO_VIEW,
      WorkspacePermissions.ACCOUNTING_JOURNAL_ENTRY_VIEW,
      WorkspacePermissions.ACCOUNTING_VIEW,
    ],
    isActive: true,
  },
  {
    key: "tradePromotions",
    label: "Trade Promotions",
    icon: <Icon name={Icons.Calendar} />,
    link: "/workspace/trade-promotions",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.TRADE_PROMOTIONS],
    isActive: true,
  },
  {
    key: "disputeTracking",
    label: "Disputes",
    icon: <Icon name={Icons.InfoHexagon} />,
    link: "/workspace/disputes-tracking",
    workspaceRequiredPermissions: [
      WorkspacePermissions.MENU_VIEW.DISPUTES_TRACKING,
    ],
    isActive: true,
  },
  {
    key: "documentRepository",
    label: "Document Repository",
    icon: <Icon name={Icons.File} />,
    link: "/workspace/document-repository",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.DOCUMENT_REPOSITORY],
    isActive: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: <Icon name={Icons.BarChart5} />,
    link: "/workspace/analytics",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.ANALYTICS],
  },
  {
    key: "users",
    label: "User Management",
    icon: <Icon name={Icons.UserManagement} />,
    link: "/workspace/users",
    workspaceRequiredPermissions: [WorkspacePermissions.MENU_VIEW.USERS],
  },
  {
    key: "purgeData",
    label: "Purge Data",
    icon: <Icon name={Icons.Trash} />,
    link: "/workspace/purge-data",
    workspaceRequiredPermissions: [WorkspacePermissions.PURGE_TRANSACTIONAL_DATA],
  },
];

const SlideNav: React.FC = () => {
  const { hasAnyPermission } = useUserPermissions();
  const { newMessageCount } = useConversations();

  const filterMenu = useCallback(() => {
    const shouldIncludeItem = (item: NavigationItemProps): boolean => {
      if (!item.workspaceRequiredPermissions || item?.isActive === false) {
        return false;
      }
      if (item.key === "disputeTracking" && !FlagsConfigInstance.getFlag(WIPFlags.DISPUTE_TRACKING)) {
        return false;
      }
      if (item.key === "home" && !FlagsConfigInstance.getFlag(WIPFlags.HOME_TODO)) {
        return false;
      }
      if (item.key === "documentRepository" && !FlagsConfigInstance.getFlag(WIPFlags.DOCUMENT_REPOSITORY)) {
        return false;
      }
      return hasAnyPermission(item.workspaceRequiredPermissions);
    };

    const processItem = (item: NavigationItemProps): NavigationItemProps => {
      const itemCopy: NavigationItemProps = { ...item };
      if (itemCopy.key === "conversations" && newMessageCount > 0) {
        itemCopy.icon = <Span className="slide-navigation-icon-with-badge">{itemCopy.icon} <Badge className="slide-navigation-icon-badge" color="processing" variant="dot" offset={[-2, -7]} ripple={true} /></Span>;
      }
      return itemCopy;
    };

    const checkPermissionsOfMenuItems = (sideNavItems: NavigationItemProps[]): NavigationItemProps[] => {
      const navItems: NavigationItemProps[] = [];
      for (const item of sideNavItems) {
        if (shouldIncludeItem(item)) {
          navItems.push(processItem(item));
        }
        if (item.children) {
          item.children = checkPermissionsOfMenuItems(item.children);
        }
      }
      return navItems;
    };

    return checkPermissionsOfMenuItems(NAVIGATION_ITEMS);
  }, [newMessageCount, hasAnyPermission]);

  const { isItemActive, handleItemClick, isCurrentLinkActive, toggleItem, isItemOpen, openItems } = useAppNavigation();

  return (
    <nav className="slide-navigation">
      <ul className="m-0 p-0">
        {filterMenu().map(item => (
          <NavigationItem
            key={item.key}
            item={item}
            isItemActive={isItemActive}
            isCurrentLinkActive={isCurrentLinkActive}
            toggleItem={toggleItem}
            isItemOpen={isItemOpen}
            handleItemClick={handleItemClick}
            openItems={openItems}
          />
        ))}
        {
          appConfig?.CURRENT_FEEDBACK !== "NONE" && (
            <li className="slide-navigation-main">
              <HasAllPermissions requiredPermissions={[WorkspacePermissions.FEEDBACK_ADD]}>
                <SidebarFeedback />
              </HasAllPermissions>
            </li>
          )
        }
      </ul>
    </nav>
  );
};

export default SlideNav;
